/**
 * Generar documentos desde una plantilla, en tres pasos:
 *   1. Datos del envío (capacitación, relator…).
 *   2. Trabajadores: filtros, búsqueda y «seleccionar los filtrados».
 *   3. Confirmar: quién recibe y quién queda fuera y por qué, con vista previa.
 *
 * 🔴 Generar es irreversible para el trabajador: si el tipo pide firma, el
 * correo sale apenas se genera. Por eso el botón final dice exactamente qué
 * hace y a cuántas personas («Generar y pedir firma (18)»), y a quienes no
 * pueden recibirlo no se les envía: se muestran con el motivo para corregirlo.
 *
 * Ruta: `/payroll/documents/generate/:templateId`.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Check, Eye, FileSignature, Send } from 'lucide-react'
import { Button } from '@/components/Button'
import { DataTable } from '@/components/ds/DataTable'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { SearchInput } from '@/components/ds/SearchInput'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { useVisorPdf } from '@/components/VisorPdf'
import { extractErrorMessage } from '@/lib/errorUtils'
import {
  pedirVistaPrevia, useCandidatos, useCrearEnvio, usePlantilla, useRevisarEnvio,
  type Candidato, type Revision,
} from '../../hooks/usePlantillasDocumentos'

const MOTIVOS_FIRMA: Record<string, string> = {
  sin_correo: 'Sin correo: no puede firmar',
  correo_en_otra_ficha: 'Su correo es el acceso de otra ficha',
}

type Paso = 1 | 2 | 3

export default function GenerarDocumentosPage() {
  const { templateId = '' } = useParams()
  const navigate = useNavigate()
  const { data: plantilla } = usePlantilla(templateId)
  const { data: candidatos = [], isLoading } = useCandidatos()
  const revisar = useRevisarEnvio(templateId)
  const crear = useCrearEnvio()

  const [paso, setPaso] = useState<Paso>(1)
  const [valores, setValores] = useState<Record<string, string>>({})
  const [elegidos, setElegidos] = useState<Set<string>>(new Set())
  const [revision, setRevision] = useState<Revision | null>(null)
  const [error, setError] = useState<string | null>(null)

  const campos = plantilla?.envio_fields ?? []
  const firma = plantilla?.requires_signature ?? false
  const faltanCampos = campos.filter((c) => c.required && !(valores[c.key] ?? '').trim())

  const irAConfirmar = () => {
    setError(null)
    revisar.mutate({ employee_ids: [...elegidos], envio_values: valores }, {
      onSuccess: (r) => { setRevision(r); setPaso(3) },
      onError: (e) => setError(extractErrorMessage(e, 'No se pudo revisar el envío.')),
    })
  }

  const generar = () => {
    if (!revision) return
    setError(null)
    const ids = revision.employees.filter((f) => f.can_generate).map((f) => f.employee_id)
    crear.mutate({ template: templateId, employee_ids: ids, envio_values: valores }, {
      onSuccess: (envio) => navigate(`/payroll/documents/batches/${envio.id}`, { replace: true }),
      onError: (e) => setError(extractErrorMessage(e, 'No se pudo generar.')),
    })
  }

  if (plantilla && plantilla.status !== 'active') {
    return (
      <div className="rounded-xl border border-divider bg-surface p-6 text-sm text-content-muted">
        La plantilla «{plantilla.name}» no está activa. Activarla desde el editor para generar documentos.
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => navigate('/payroll/documents')} aria-label="Volver a Documentos"
          className="p-1.5 rounded-lg hover:bg-soft text-content-muted"><ArrowLeft size={18} /></button>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-content truncate">Generar: {plantilla?.name ?? '…'}</h2>
          <p className="text-xs text-content-muted">
            {firma ? 'Cada persona recibe un correo para firmarlo desde su portal.' : 'No pide firma: queda en la ficha y en el portal.'}
          </p>
        </div>
      </div>

      <Pasos actual={paso} />
      {error && <p role="alert" className="text-sm text-danger-content">{error}</p>}

      {paso === 1 && (
        <section className="rounded-xl border border-divider bg-surface p-4 sm:p-5 space-y-4">
          {campos.length === 0
            ? <p className="text-sm text-content-muted">Esta plantilla no tiene campos del envío: todo sale de la ficha de cada persona.</p>
            : (
              <div className="grid gap-4 sm:grid-cols-2">
                {campos.map((c) => (
                  <div key={c.key} className={c.multiline ? 'sm:col-span-2' : ''}>
                    <TextField dense label={c.label} required={c.required} value={valores[c.key] ?? ''}
                      multiline={c.multiline} rows={c.multiline ? 3 : undefined}
                      onChange={(e) => setValores({ ...valores, [c.key]: e.target.value })} />
                  </div>
                ))}
              </div>
            )}
          <div className="flex justify-end">
            <Button disabled={faltanCampos.length > 0} onClick={() => setPaso(2)}
              title={faltanCampos.length ? `Falta: ${faltanCampos.map((c) => c.label).join(', ')}` : undefined}>
              Siguiente: trabajadores
            </Button>
          </div>
        </section>
      )}

      {paso === 2 && (
        <ElegirTrabajadores candidatos={candidatos} cargando={isLoading} firma={firma}
          elegidos={elegidos} onChange={setElegidos}
          onAtras={() => setPaso(1)} onSiguiente={irAConfirmar} revisando={revisar.isPending} />
      )}

      {paso === 3 && revision && (
        <Confirmar revision={revision} firma={firma} plantillaId={templateId}
          nombre={plantilla?.name ?? ''} valores={valores}
          onAtras={() => setPaso(2)} onGenerar={generar} generando={crear.isPending} />
      )}
    </div>
  )
}

function Pasos({ actual }: { actual: Paso }) {
  const pasos = ['Datos del envío', 'Trabajadores', 'Confirmar']
  return (
    <ol className="flex flex-wrap items-center gap-2 text-sm" aria-label="Pasos">
      {pasos.map((nombre, i) => {
        const n = (i + 1) as Paso
        const hecho = n < actual
        return (
          <li key={nombre} className="flex items-center gap-2" aria-current={n === actual ? 'step' : undefined}>
            <span className={`w-6 h-6 rounded-full grid place-items-center text-xs font-semibold
              ${n === actual ? 'bg-primary text-on-primary' : hecho ? 'bg-success-surface text-success-content' : 'bg-soft text-content-muted'}`}>
              {hecho ? <Check size={13} /> : n}
            </span>
            <span className={n === actual ? 'text-content font-medium' : 'text-content-muted'}>{nombre}</span>
            {i < pasos.length - 1 && <span className="w-6 h-px bg-divider mx-1" aria-hidden="true" />}
          </li>
        )
      })}
    </ol>
  )
}

// ─── Paso 2 ───────────────────────────────────────────────────────────────────

function ElegirTrabajadores({ candidatos, cargando, firma, elegidos, onChange, onAtras, onSiguiente, revisando }: {
  candidatos: Candidato[]; cargando: boolean; firma: boolean
  elegidos: Set<string>; onChange: (s: Set<string>) => void
  onAtras: () => void; onSiguiente: () => void; revisando: boolean
}) {
  const [buscar, setBuscar] = useState('')
  const [area, setArea] = useState('')
  const [cargo, setCargo] = useState('')
  const [centro, setCentro] = useState('')

  const opciones = (campo: 'area' | 'position' | 'cost_center') =>
    [...new Set(candidatos.map((c) => c[campo]).filter(Boolean))].sort().map((v) => ({ value: v, label: v }))

  const filtrados = useMemo(() => {
    const q = buscar.trim().toLowerCase()
    return candidatos.filter((c) =>
      (!q || c.name.toLowerCase().includes(q) || c.rut.toLowerCase().includes(q))
      && (!area || c.area === area) && (!cargo || c.position === cargo) && (!centro || c.cost_center === centro))
  }, [candidatos, buscar, area, cargo, centro])

  const todosFiltrados = filtrados.length > 0 && filtrados.every((c) => elegidos.has(c.id))
  const alternarFiltrados = () => {
    const s = new Set(elegidos)
    filtrados.forEach((c) => (todosFiltrados ? s.delete(c.id) : s.add(c.id)))
    onChange(s)
  }
  const alternar = (id: string) => {
    const s = new Set(elegidos)
    if (s.has(id)) s.delete(id)
    else s.add(id)
    onChange(s)
  }

  return (
    <section className="space-y-3">
      <div className="rounded-xl border border-divider bg-surface p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 items-end">
        <SearchInput label="Buscar" placeholder="Nombre o RUT" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
        <SelectField dense label="Área" value={area} onChange={(e) => setArea(e.target.value)}
          placeholder="Todas" options={opciones('area')} />
        <SelectField dense label="Cargo" value={cargo} onChange={(e) => setCargo(e.target.value)}
          placeholder="Todos" options={opciones('position')} />
        <SelectField dense label="Centro de costo" value={centro} onChange={(e) => setCentro(e.target.value)}
          placeholder="Todos" options={opciones('cost_center')} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <span className="text-content-muted">
          <strong className="text-content">{elegidos.size}</strong> {elegidos.size === 1 ? 'persona elegida' : 'personas elegidas'}
          {filtrados.length !== candidatos.length && ` · ${filtrados.length} en el filtro`}
        </span>
        <Button size="sm" variant="secondary" onClick={alternarFiltrados} disabled={filtrados.length === 0}>
          {todosFiltrados ? 'Quitar los filtrados' : `Elegir los filtrados (${filtrados.length})`}
        </Button>
      </div>

      <DataTable<Candidato>
        rows={filtrados}
        rowKey={(c) => c.id}
        loading={cargando}
        density="dense"
        onRowClick={(c) => alternar(c.id)}
        rowClassName={(c) => (elegidos.has(c.id) ? 'bg-primary-50/60' : undefined)}
        columns={[
          {
            key: 'sel', width: 40,
            header: <input type="checkbox" aria-label="Elegir todos los filtrados" checked={todosFiltrados}
              onChange={alternarFiltrados} className="rounded border-divider-strong" />,
            render: (c) => <input type="checkbox" aria-label={`Elegir a ${c.name}`} checked={elegidos.has(c.id)}
              onChange={() => alternar(c.id)} onClick={(e) => e.stopPropagation()} className="rounded border-divider-strong" />,
          },
          {
            key: 'name', header: 'Nombre', sortable: true, sortValue: (c) => c.name.toLowerCase(),
            render: (c) => (<div><div className="text-content">{c.name}</div><div className="text-xs text-content-muted">{c.rut}</div></div>),
          },
          { key: 'position', header: 'Cargo', sortable: true, render: (c) => c.position || '—' },
          { key: 'area', header: 'Área', sortable: true, render: (c) => c.area || '—' },
          { key: 'cost_center', header: 'Centro de costo', render: (c) => c.cost_center || '—' },
          {
            key: 'aviso', header: '',
            render: (c) => firma && c.signature_issue
              ? <StatusBadge status="warning" tone="warning" label={MOTIVOS_FIRMA[c.signature_issue] ?? 'No puede firmar'} />
              : c.status === 'on_leave' ? <StatusBadge status="leave" tone="neutral" label="Con licencia" /> : null,
          },
        ]}
      />

      <div className="flex justify-between">
        <Button variant="secondary" onClick={onAtras}>Atrás</Button>
        <Button disabled={elegidos.size === 0} isPending={revisando} onClick={onSiguiente}>
          Siguiente: confirmar ({elegidos.size})
        </Button>
      </div>
    </section>
  )
}

// ─── Paso 3 ───────────────────────────────────────────────────────────────────

function Confirmar({ revision, firma, plantillaId, nombre, valores, onAtras, onGenerar, generando }: {
  revision: Revision; firma: boolean; plantillaId: string; nombre: string; valores: Record<string, string>
  onAtras: () => void; onGenerar: () => void; generando: boolean
}) {
  const { ver, visor } = useVisorPdf()
  const reciben = revision.employees.filter((f) => f.can_generate)
  const fuera = revision.employees.filter((f) => !f.can_generate)
  const [muestra, setMuestra] = useState(reciben[0]?.employee_id ?? '')
  useEffect(() => { if (!muestra && reciben[0]) setMuestra(reciben[0].employee_id) }, [muestra, reciben])
  const persona = reciben.find((f) => f.employee_id === muestra)

  return (
    <section className="space-y-4">
      <div className="rounded-xl border border-divider bg-surface p-4 sm:p-5 space-y-3">
        <p className="text-content">
          Se {reciben.length === 1 ? 'genera' : 'generan'} <strong>{reciben.length}</strong>{' '}
          {reciben.length === 1 ? 'documento' : 'documentos'}
          {firma && ' y cada persona recibe un correo para firmarlo'}.
          {fuera.length > 0 && <> <strong>{fuera.length}</strong> {fuera.length === 1 ? 'persona queda' : 'personas quedan'} fuera.</>}
        </p>
        {reciben.length > 0 && (
          <div className="flex flex-wrap items-end gap-3">
            <SelectField dense label="Vista previa de" value={muestra} onChange={(e) => setMuestra(e.target.value)}
              options={reciben.map((f) => ({ value: f.employee_id, label: f.name }))} />
            <Button variant="secondary" disabled={!muestra} onClick={() => ver({
              titulo: `${nombre} — ${persona?.name ?? ''}`, nombreArchivo: 'vista-previa.pdf',
              cargar: () => pedirVistaPrevia(plantillaId, { employee_id: muestra, envio_values: valores }),
            })}><Eye size={14} /> Ver</Button>
          </div>
        )}
      </div>

      {fuera.length > 0 && (
        <div className="rounded-xl border border-warning-border bg-warning-surface p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-warning-content">
            <AlertTriangle size={16} /> No se les genera hasta corregirlo
          </div>
          <ul className="text-sm text-content space-y-1">
            {fuera.map((f) => (
              <li key={f.employee_id}><strong>{f.name}</strong> ({f.rut}): {f.message}</li>
            ))}
          </ul>
          <p className="text-xs text-content-muted">
            Después de completar su ficha, generar de nuevo eligiendo solo a esas personas.
          </p>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="secondary" onClick={onAtras}>Atrás</Button>
        <Button disabled={reciben.length === 0} isPending={generando} onClick={onGenerar}>
          {firma ? <FileSignature size={14} /> : <Send size={14} />}
          {firma ? ` Generar y pedir firma (${reciben.length})` : ` Generar (${reciben.length})`}
        </Button>
      </div>
      {visor}
    </section>
  )
}
